import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useCallback } from "react";

export const useFurniture = (propertyId = null) => {
    const { user } = useAuth();
    const queryClient = useQueryClient();

    // 1. Fetching Logic (Query)
    const { data: furniture = [], isLoading: loading, refetch } = useQuery({
        queryKey: ["furniture", propertyId],
        queryFn: async () => {
            if (!user || !propertyId) return [];
            const { data, error } = await supabase
                .from("furniture")
                .select("*")
                .eq("property_id", propertyId)
                .order("created_at", { ascending: false });

            if (error) throw error;
            return data || [];
        },
        enabled: !!user && !!propertyId,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    });

    // 2. Delete Mutation
    const deleteMutation = useMutation({
        mutationFn: async (id) => {
            const { error } = await supabase.from("furniture").delete().eq("id", id);
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["furniture", propertyId] });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to delete item.",
                variant: "destructive",
            });
        }
    });

    // 3. Update Mutation
    const updateMutation = useMutation({
        mutationFn: async ({ id, updates }) => {
            const { error } = await supabase
                .from("furniture")
                .update(updates)
                .eq("id", id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["furniture", propertyId] });
        },
        onError: () => {
            toast({
                title: "Error",
                description: "Failed to update item.",
                variant: "destructive",
            });
        }
    });

    const getExpiringWarranties = useCallback(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const thirtyDaysFromNow = new Date();
        thirtyDaysFromNow.setDate(today.getDate() + 30);
        thirtyDaysFromNow.setHours(23, 59, 59, 999);

        return furniture.filter((item) => {
            if (!item.warranty_expiry || item.warranty_dismissed) return false;
            const expiryDate = new Date(item.warranty_expiry);
            expiryDate.setHours(0, 0, 0, 0);
            return expiryDate <= thirtyDaysFromNow;
        });
    }, [furniture]);

    const dismissWarranty = useCallback(async (id) => {
        return updateMutation.mutateAsync({ id, updates: { warranty_dismissed: true } })
            .then(() => true)
            .catch(() => false);
    }, [updateMutation]);

    return {
        furniture,
        loading,
        refetch,
        deleteFurniture: (id) => deleteMutation.mutateAsync(id).then(() => true).catch(() => false),
        updateFurniture: (id, updates) => updateMutation.mutateAsync({ id, updates }).then(() => true).catch(() => false),
        getExpiringWarranties,
        dismissWarranty
    };
};
